import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, GripVertical, X, Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const emptyForm = {
  name_es: '', name_en: '', name_de: '',
  description_es: '', description_en: '', description_de: '',
  duration: 'dia entero', distance_km: 0, difficulty: 'medium',
  image: '', stops: [],
};

const AdminItineraries = ({ places = [] }) => {
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(null);
  const [translatingId, setTranslatingId] = useState(null);

  useEffect(() => { loadItineraries(); }, []);

  const loadItineraries = async () => {
    try {
      const res = await api.getItineraries();
      setItineraries(res.data);
    } catch (e) {
      toast.error('Error al cargar itinerarios');
    } finally { setLoading(false); }
  };

  const openEditor = (itin = null) => {
    if (itin) {
      setEditingId(itin.id);
      setFormData({
        name_es: itin.name_es || '', name_en: itin.name_en || '', name_de: itin.name_de || '',
        description_es: itin.description_es || '', description_en: itin.description_en || '', description_de: itin.description_de || '',
        duration: itin.duration || 'dia entero', distance_km: itin.distance_km || 0,
        difficulty: itin.difficulty || 'medium', image: itin.image || '',
        stops: (itin.stops || []).map((s, i) => ({
          place_id: s.place_id || s.place?.id || '',
          order: s.order ?? i,
          tip_es: s.tip_es || '', tip_en: s.tip_en || '', tip_de: s.tip_de || '',
        })),
      });
    } else {
      setEditingId(null);
      setFormData(emptyForm);
    }
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.name_es) { toast.error('Nombre ES obligatorio'); return; }
    if (formData.stops.length === 0) { toast.error('Anade al menos una parada'); return; }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        stops: formData.stops.filter(s => s.place_id).map((s, i) => ({ ...s, order: i })),
      };
      if (editingId) {
        await api.updateItinerary(editingId, payload);
        toast.success('Itinerario actualizado');
      } else {
        await api.createItinerary(payload);
        toast.success('Itinerario creado');
      }
      setShowEditor(false);
      loadItineraries();
    } catch (e) {
      toast.error('Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteItinerary(id);
      toast.success('Itinerario eliminado');
      setShowDelete(null);
      loadItineraries();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const addStop = () => {
    setFormData(prev => ({
      ...prev,
      stops: [...prev.stops, { place_id: '', order: prev.stops.length, tip_es: '', tip_en: '', tip_de: '' }]
    }));
  };

  const updateStop = (idx, field, val) => {
    setFormData(prev => ({
      ...prev,
      stops: prev.stops.map((s, i) => i === idx ? { ...s, [field]: val } : s)
    }));
  };

  const removeStop = (idx) => {
    setFormData(prev => ({ ...prev, stops: prev.stops.filter((_, i) => i !== idx) }));
  };

  const getPlaceName = (placeId) => {
    const p = places.find(p => p.id === placeId);
    return p ? p.name_es : placeId;
  };

  const handleTranslate = async (itinId) => {
    setTranslatingId(itinId);
    try {
      const res = await api.translateItinerary(itinId);
      if (res.data.success) {
        toast.success(`Itinerario traducido (${res.data.translated_fields.length} campos)`);
        loadItineraries();
      } else {
        toast.warning(res.data.message || 'No se pudo traducir');
      }
    } catch (e) {
      toast.error('Error al traducir: ' + (e.response?.data?.detail || e.message));
    } finally { setTranslatingId(null); }
  };

  const handleTranslateAll = async () => {
    const untranslated = itineraries.filter(i => !i.name_en || !i.name_de);
    if (untranslated.length === 0) {
      toast.info('Todos los itinerarios ya estan traducidos');
      return;
    }
    if (!window.confirm(`¿Traducir ${untranslated.length} itinerarios sin traducir?`)) return;
    
    setTranslatingId('all');
    try {
      const res = await api.translateAllItineraries();
      if (res.data.success) {
        toast.success(`Traducidos ${res.data.translated} itinerarios`);
        loadItineraries();
      }
    } catch (e) {
      toast.error('Error al traducir: ' + (e.response?.data?.detail || e.message));
    } finally { setTranslatingId(null); }
  };

  // Count untranslated
  const untranslatedCount = itineraries.filter(i => !i.name_en || !i.name_de).length;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={24} /></div>;

  return (
    <div data-testid="admin-itineraries">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <p className="text-sm text-gray-500">{itineraries.length} itinerarios</p>
          {untranslatedCount > 0 && (
            <Button 
              onClick={handleTranslateAll} 
              disabled={translatingId === 'all'}
              className="bg-blue-500 hover:bg-blue-600 rounded-full text-sm" 
              data-testid="translate-all-itineraries-btn"
            >
              {translatingId === 'all' ? <Loader2 className="animate-spin mr-1" size={14} /> : <Languages size={14} className="mr-1" />}
              Traducir {untranslatedCount}
            </Button>
          )}
          <Button onClick={() => openEditor()} className="bg-[#38B2AC] hover:bg-[#319795] rounded-full" data-testid="add-itinerary-btn">
            <Plus size={16} className="mr-1" /> Itinerario
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {itineraries.map(itin => (
          <div key={itin.id} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-gray-100" data-testid={`itin-row-${itin.id}`}>
            {itin.image && <img src={itin.image} alt="" className="w-14 h-14 object-cover flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-[#1D1D1F] truncate">{itin.name_es}</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">{itin.stops?.length || 0} paradas · {itin.duration} · {itin.distance_km} km</span>
                {(!itin.name_en || !itin.name_de) && (
                  <span className="text-xs text-amber-500">Sin traducir</span>
                )}
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => handleTranslate(itin.id)} 
              disabled={translatingId === itin.id}
              title="Traducir a EN/DE"
              data-testid={`translate-itin-${itin.id}`}
            >
              {translatingId === itin.id ? <Loader2 size={16} className="animate-spin" /> : <Languages size={16} className="text-blue-500" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => openEditor(itin)} data-testid={`edit-itin-${itin.id}`}>
              <Edit size={16} />
            </Button>
            <Button variant="ghost" size="icon" className="text-red-500" onClick={() => setShowDelete(itin)} data-testid={`delete-itin-${itin.id}`}>
              <Trash2 size={16} />
            </Button>
          </div>
        ))}
        {itineraries.length === 0 && <p className="text-center text-gray-400 py-8 text-sm">No hay itinerarios</p>}
      </div>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar itinerario' : 'Nuevo itinerario'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Nombre (ES) *</Label><Input value={formData.name_es} onChange={e => setFormData(p => ({ ...p, name_es: e.target.value }))} data-testid="itin-name-es" /></div>
              <div><Label>Name (EN)</Label><Input value={formData.name_en} onChange={e => setFormData(p => ({ ...p, name_en: e.target.value }))} /></div>
              <div><Label>Name (DE)</Label><Input value={formData.name_de} onChange={e => setFormData(p => ({ ...p, name_de: e.target.value }))} /></div>
            </div>
            <div><Label>Descripcion (ES)</Label><Textarea rows={2} value={formData.description_es} onChange={e => setFormData(p => ({ ...p, description_es: e.target.value }))} /></div>
            <div><Label>Imagen URL</Label><Input value={formData.image} onChange={e => setFormData(p => ({ ...p, image: e.target.value }))} placeholder="https://..." /></div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Duracion</Label>
                <select value={formData.duration} onChange={e => setFormData(p => ({ ...p, duration: e.target.value }))}
                  className="flex h-9 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                  <option value="medio dia">Medio dia</option>
                  <option value="dia entero">Dia entero</option>
                  <option value="2 dias">2 dias</option>
                </select>
              </div>
              <div><Label>Distancia (km)</Label><Input type="number" value={formData.distance_km} onChange={e => setFormData(p => ({ ...p, distance_km: parseFloat(e.target.value) || 0 }))} /></div>
              <div>
                <Label>Dificultad</Label>
                <select value={formData.difficulty} onChange={e => setFormData(p => ({ ...p, difficulty: e.target.value }))}
                  className="flex h-9 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                  <option value="easy">Facil</option>
                  <option value="medium">Media</option>
                  <option value="hard">Dificil</option>
                </select>
              </div>
            </div>

            {/* Stops */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Paradas</Label>
                <Button variant="outline" size="sm" onClick={addStop}><Plus size={14} className="mr-1" /> Parada</Button>
              </div>
              {formData.stops.map((stop, idx) => (
                <div key={idx} className="flex gap-2 mb-3 items-start p-3 bg-gray-50 rounded-lg">
                  <span className="text-xs font-bold text-gray-400 mt-2">{idx + 1}</span>
                  <div className="flex-1 space-y-2">
                    <select value={stop.place_id} onChange={e => updateStop(idx, 'place_id', e.target.value)}
                      className="flex h-9 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm" data-testid={`stop-place-${idx}`}>
                      <option value="">Seleccionar lugar...</option>
                      {places.map(p => <option key={p.id} value={p.id}>{p.name_es}</option>)}
                    </select>
                    <Input placeholder="Tip (ES)" value={stop.tip_es} onChange={e => updateStop(idx, 'tip_es', e.target.value)} />
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeStop(idx)} className="mt-1"><X size={14} /></Button>
                </div>
              ))}
              {formData.stops.length === 0 && <p className="text-xs text-gray-400 text-center py-4">Anade paradas seleccionando lugares</p>}
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#38B2AC] hover:bg-[#319795]" data-testid="save-itin-btn">
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                {editingId ? 'Guardar cambios' : 'Crear itinerario'}
              </Button>
              <Button variant="outline" onClick={() => setShowEditor(false)} className="flex-1">Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!showDelete} onOpenChange={() => setShowDelete(null)}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader><DialogTitle>Eliminar itinerario</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">¿Seguro que quieres eliminar "{showDelete?.name_es}"?</p>
          <div className="flex gap-3 mt-4">
            <Button variant="destructive" onClick={() => handleDelete(showDelete.id)} className="flex-1" data-testid="confirm-delete-itin">Eliminar</Button>
            <Button variant="outline" onClick={() => setShowDelete(null)} className="flex-1">Cancelar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminItineraries;
